import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FingerprintIcon, Loader2Icon } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { PIN_LENGTH, PinPad } from '@/components/pin-pad';
import { Button } from '@/components/ui/button';
import { Alert, Field, Input } from '@/components/ui/form';
import { biometricCapability, loginWithBiometric } from '@/lib/auth/biometric';
import { type PendingLogin, useAuth } from '@/lib/auth/context';
import { percent } from '@/lib/format';
import { completeOnlineAuth, patchEnvelope, readEnvelope } from '@/lib/session';
import { useOnline } from '@/lib/use-online';
import { unlockDataKey } from '@/offline/crypto';
import { run, type SyncProgress, subscribeSyncProgress } from '@/offline/sync-engine';

export const Route = createFileRoute('/masuk')({
  component: LoginPage,
});

type Stage = 'credentials' | 'pin' | 'download';

/**
 * Login lapangan (T-P7-04, FR-AUTH-01, FR-SYNC-03).
 *
 * Tiga langkah, dan urutannya penting: kredensial membuktikan siapa, PIN membuka
 * (atau membuat) kunci cache lokal, lalu dataset awal diunduh. Mengunduh sebelum
 * kunci ada berarti data nasabah sempat tertulis tanpa enkripsi.
 */
function LoginPage() {
  const { status, beginLogin, finishLogin } = useAuth();
  const navigate = useNavigate();
  const online = useOnline();

  const [stage, setStage] = useState<Stage>('credentials');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState<PendingLogin | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);

  useEffect(() => {
    if (status === 'authenticated') void navigate({ to: '/', replace: true });
  }, [status, navigate]);

  useEffect(() => {
    void readEnvelope().then((envelope) => {
      if (!envelope) return;
      setUsername(envelope.username);
      if (envelope.biometricEnabled)
        void biometricCapability().then((c) => setBiometricAvailable(c.supported));
    });
  }, []);

  async function onCredentials(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      const result = await beginLogin(username.trim(), password);
      setPending(result);
      setPassword('');
      setStage('pin');
    } catch (caught) {
      // Pesan server sengaja tidak membedakan username salah dari password salah.
      setError(caught instanceof Error ? caught.message : 'Login gagal. Coba lagi.');
    } finally {
      setBusy(false);
    }
  }

  async function onBiometricLogin() {
    setError(null);
    setBusy(true);

    try {
      const { session, prf } = await loginWithBiometric(username.trim());
      const opened = prf !== null && (await unlockDataKey({ method: 'prf', output: prf }));

      if (!opened) {
        // Biometrik membuktikan identitas ke server, tetapi tanpa PRF ia tidak
        // menurunkan kunci cache — PIN tetap satu-satunya jalan membukanya.
        setPending({ session, needsPinSetup: false });
        setStage('pin');
        return;
      }

      await completeOnlineAuth({
        user: session.user,
        accessToken: session.tokens.accessToken,
        offlineGrantExpiresAt: session.tokens.offlineGrantExpiresAt,
        deviceId: session.deviceId ?? null,
      });

      setStage('download');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Login biometrik gagal.');
    } finally {
      setBusy(false);
    }
  }

  if (stage === 'download')
    return <BootstrapDownload onDone={() => navigate({ to: '/', replace: true })} />;

  if (stage === 'pin' && pending) {
    return (
      <PinStage
        pending={pending}
        onBack={() => setStage('credentials')}
        onDone={async (result) => {
          await finishLogin(pending, result);
          setStage('download');
        }}
      />
    );
  }

  return (
    <main className="flex min-h-dvh flex-col justify-center gap-6 px-6 py-10">
      <header className="space-y-1 text-center">
        <h1 className="text-xl font-semibold tracking-tight">BSSB Mobile Collection</h1>
        <p className="text-sm text-muted-foreground">Masuk untuk mulai menagih.</p>
      </header>

      {!online ? (
        <Alert variant="warning">
          Perangkat sedang offline. Login pertama pada perangkat ini memerlukan koneksi.
        </Alert>
      ) : null}

      <form onSubmit={onCredentials} className="space-y-4" noValidate>
        {error ? <Alert variant="danger">{error}</Alert> : null}

        <Field id="username" label="Username" required>
          <Input
            id="username"
            name="username"
            autoComplete="username"
            autoCapitalize="none"
            required
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />
        </Field>

        <Field id="password" label="Password" required>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        <Button type="submit" size="lg" fullWidth disabled={busy || !online}>
          {busy ? <Loader2Icon className="size-4 animate-spin" aria-hidden="true" /> : null}
          Masuk
        </Button>
      </form>

      {biometricAvailable && online ? (
        <Button
          variant="secondary"
          size="lg"
          fullWidth
          disabled={busy}
          onClick={() => void onBiometricLogin()}
          leading={<FingerprintIcon className="size-5" aria-hidden="true" />}
        >
          Masuk dengan biometrik
        </Button>
      ) : null}

      <p className="text-center text-xs text-muted-foreground">
        Lupa password? Hubungi admin cabang Anda.
      </p>
    </main>
  );
}

function PinStage({
  pending,
  onBack,
  onDone,
}: {
  pending: PendingLogin;
  onBack: () => void;
  onDone: (pin: string) => Promise<void>;
}) {
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const confirming = pending.needsPinSetup && confirm !== null;

  async function submit() {
    if (pin.length !== PIN_LENGTH) return;

    if (pending.needsPinSetup && confirm === null) {
      // Pembuatan PIN diketik dua kali: PIN yang salah ketik baru ketahuan saat
      // aplikasi dibuka offline, dan pada saat itu cache sudah tidak terbuka lagi.
      setConfirm(pin);
      setPin('');
      return;
    }

    if (confirming && pin !== confirm) {
      setError('PIN tidak sama. Ulangi dari awal.');
      setConfirm(null);
      setPin('');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      await onDone(pin);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'PIN salah.');
      setPin('');
    } finally {
      setBusy(false);
    }
  }

  const label = !pending.needsPinSetup
    ? 'Masukkan PIN untuk membuka data di perangkat ini'
    : confirming
      ? 'Ulangi PIN yang sama'
      : 'Buat PIN 6 digit untuk membuka data secara offline';

  return (
    <main className="flex min-h-dvh flex-col justify-center gap-8 px-6 py-10">
      <PinPad
        value={pin}
        onChange={(next) => {
          setError(null);
          setPin(next);
        }}
        onSubmit={() => void submit()}
        disabled={busy}
        label={label}
        error={error}
      />

      <Button variant="ghost" fullWidth onClick={onBack} disabled={busy}>
        Kembali
      </Button>
    </main>
  );
}

/**
 * Unduhan dataset awal (FR-SYNC-03).
 *
 * Kemajuan dilaporkan per entitas, bukan per baris: jumlah baris total tidak
 * diketahui sebelum halaman terakhir tiba, dan bilah yang melompat mundur lebih
 * membingungkan daripada bilah yang bergerak kasar.
 */
function BootstrapDownload({ onDone }: { onDone: () => void }) {
  const [progress, setProgress] = useState<SyncProgress | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeSyncProgress(setProgress);

    void run()
      .then(async (report) => {
        if (report.errors.length > 0 && report.pulled.customers === undefined) {
          setFailed(report.errors[0] ?? 'Unduhan gagal');
          return;
        }
        await patchEnvelope({ bootstrapped: true });
        onDone();
      })
      .catch((error: unknown) =>
        setFailed(error instanceof Error ? error.message : 'Unduhan gagal'),
      );

    return unsubscribe;
  }, [onDone]);

  const ratio =
    progress?.phase === 'pull' && progress.entityCount > 0
      ? progress.entityIndex / progress.entityCount
      : progress?.phase === 'done'
        ? 1
        : 0;

  return (
    <main className="flex min-h-dvh flex-col justify-center gap-6 px-6 py-10">
      <div className="space-y-2 text-center">
        <h1 className="text-lg font-semibold">Menyiapkan data</h1>
        <p className="text-sm text-muted-foreground">
          Data nasabah diunduh sekali, lalu aplikasi bekerja tanpa sinyal.
        </p>
      </div>

      {failed ? (
        <>
          <Alert variant="danger">{failed}</Alert>
          <Button size="lg" fullWidth onClick={onDone}>
            Lanjutkan tanpa unduhan penuh
          </Button>
        </>
      ) : (
        <div className="space-y-2">
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(ratio * 100)}
            aria-label="Kemajuan unduhan data"
            className="h-2 overflow-hidden rounded-full bg-muted"
          >
            <div
              className="h-full rounded-full bg-brand transition-[width]"
              style={{ width: `${ratio * 100}%` }}
            />
          </div>

          <p className="text-center text-xs text-muted-foreground">
            {progress?.phase === 'pull'
              ? `Mengunduh ${ENTITY_LABELS[progress.entity ?? 'customers']} — ${percent(ratio, 0)}`
              : 'Menghubungi server…'}
          </p>
        </div>
      )}
    </main>
  );
}

const ENTITY_LABELS: Record<string, string> = {
  customers: 'data nasabah',
  loans: 'data pinjaman',
  addresses: 'alamat',
  contacts: 'kontak',
  collaterals: 'agunan',
  paymentHistory: 'riwayat pembayaran',
  visitHistory: 'riwayat kunjungan',
};
